import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Card,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  IconButton
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ConfirmContext';
import { format } from 'date-fns';

interface ChatMessage {
  id: number;
  senderId: string;
  receiverId: string;
  messageText: string;
  createdAt: string;
  isRead: boolean;
}

export const StudentChat: React.FC = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instructor, setInstructor] = useState<{ id: string; name: string } | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessageText, setNewMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatData = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      else setRefreshing(true);

      // 1. Fetch student progress to find the instructor
      const progressRes = await api.getStudentLessonsProgress();
      const progressData = progressRes.data;

      if (!progressData.instructorUserId) {
        setInstructor(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      setInstructor({
        id: progressData.instructorUserId,
        name: progressData.instructorName || 'My Instructor'
      });

      // 2. Fetch history with the instructor
      const historyRes = await api.getChatHistory(progressData.instructorUserId);
      setMessages(historyRes.data || []);
      
    } catch (err: any) {
      console.error('Failed to load chat:', err);
      setError(err.response?.data?.message || 'Failed to load chat history.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchChatData();
  }, [fetchChatData]);

  // Scroll on messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Setup 5-second polling for real-time messages
  useEffect(() => {
    if (instructor?.id) {
      pollIntervalRef.current = setInterval(() => {
        fetchChatData(true);
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [instructor?.id, fetchChatData]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessageText.trim() || !instructor?.id || sending) return;

    try {
      setSending(true);
      const res = await api.sendChatMessage(instructor.id, newMessageText.trim());
      
      // Append the new message to state immediately for responsiveness
      setMessages(prev => [...prev, res.data]);
      setNewMessageText('');
      scrollToBottom();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to send message.', 'error');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header back button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/student/dashboard')}
          sx={{ fontWeight: 'bold' }}
        >
          Back to Dashboard
        </Button>
        {instructor && (
          <IconButton onClick={() => fetchChatData(true)} disabled={refreshing} size="small" color="primary">
            <RefreshIcon className={refreshing ? 'spin' : ''} />
          </IconButton>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {!instructor ? (
        <Card sx={{ borderRadius: 3, border: '1px dashed #ccc', p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Instructor Assigned
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have a driving instructor assigned to your profile yet. Once your instructor is assigned, you will be able to chat with them here.
          </Typography>
        </Card>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid #eef2f6',
            display: 'flex',
            flexDirection: 'column',
            height: '70vh',
            overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(0,0,0,0.02)'
          }}
        >
          {/* Chat Header */}
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fafafa', borderBottom: '1px solid #eef2f6' }}>
            <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 'bold' }}>
              {instructor.name[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {instructor.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Your Assigned Driving Instructor
              </Typography>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box
            sx={{
              flexGrow: 1,
              p: 3,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              bgcolor: '#fcfcfc'
            }}
          >
            {messages.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                <Typography variant="body2" color="text.secondary">
                  No messages yet. Send a message to start the conversation!
                </Typography>
              </Box>
            ) : (
              messages.map((msg) => {
                const isCurrentUserSender = msg.senderId !== instructor.id;
                return (
                  <Box
                    key={msg.id}
                    sx={{
                      display: 'flex',
                      justifyContent: isCurrentUserSender ? 'flex-end' : 'flex-start',
                      width: '100%'
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isCurrentUserSender ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.8,
                          borderRadius: isCurrentUserSender
                            ? '18px 18px 0px 18px'
                            : '18px 18px 18px 0px',
                          bgcolor: isCurrentUserSender ? 'primary.main' : '#f0f2f5',
                          color: isCurrentUserSender ? 'primary.contrastText' : 'text.primary',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {msg.messageText}
                        </Typography>
                      </Paper>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1, fontSize: '0.7rem' }}>
                        {format(new Date(msg.createdAt), 'HH:mm')}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {/* Input Area */}
          <Box
            component="form"
            onSubmit={handleSendMessage}
            sx={{
              p: 2,
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              bgcolor: '#fafafa'
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Type your message to instructor..."
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              disabled={sending}
              autoComplete="off"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 6,
                  bgcolor: '#fff'
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!newMessageText.trim() || sending}
              sx={{
                minWidth: 44,
                width: 44,
                height: 44,
                borderRadius: '50%',
                p: 0,
                boxShadow: 'none'
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </Button>
          </Box>
        </Paper>
      )}

      {/* CSS styling for refresh spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};

export default StudentChat;
